import React, {FC, useEffect, useMemo, useState} from 'react'
import type {Vessel} from '../../Management/core/_models'
import {
  deleteWorkPermitAttachment,
  listCrewsLite,
  type WorkPermitAttachmentType,
} from '../core/_requests'
import type {CrewLite} from '../core/_models'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import {STATIC_FORMS} from '../../Technical/components/Forms/staticFormsConfig'

export type WorkPermitFormState = {
  vesselId: number | null
  permitName: string
  formNumber: string
  startDate: string
  startTime: string
  expectedEndDate: string
  expectedEndTime: string
  endDate: string
  endTime: string
  personResponsible: string
  personResponsibleRank: string
  leadPerson: string
  leadPersonRank: string
  officePermissionRequired: boolean
  officeApproved: boolean
  officeRemarks: string
  permitFormFiles: {name: string}[]
  officePermitFiles: {name: string}[]
  completedFormFiles: {name: string}[]
  status?: 'SAVE' | 'SUBMIT' | 'CLOSE'
}

export type WorkPermitUploadFiles = {
  permitFormFiles: File[]
  officePermitFiles: File[]
  completedFormFiles: File[]
}

type CompanyGroup = {id: number; name: string}
type Subcompany = {id: number; name: string; companyId: number}

type WorkPermitExistingAttachments = {
  permitFormFiles: {name: string}[]
  officePermitFiles: {name: string}[]
  completedFormFiles: {name: string}[]
}

const emptyUploads: WorkPermitUploadFiles = {
  permitFormFiles: [],
  officePermitFiles: [],
  completedFormFiles: [],
}

const emptyExistingAttachments: WorkPermitExistingAttachments = {
  permitFormFiles: [],
  officePermitFiles: [],
  completedFormFiles: [],
}

const emptyForm: WorkPermitFormState = {
  vesselId: null,
  permitName: '',
  formNumber: '',
  startDate: '',
  startTime: '',
  expectedEndDate: '',
  expectedEndTime: '',
  endDate: '',
  endTime: '',
  personResponsible: '',
  personResponsibleRank: '',
  leadPerson: '',
  leadPersonRank: '',
  officePermissionRequired: false,
  officeApproved: false,
  officeRemarks: '',
  permitFormFiles: [],
  officePermitFiles: [],
  completedFormFiles: [],
  status: 'SAVE',
}

const attachmentsTooltip = (items: {name: string}[] | undefined | null) => {
  if (!items || items.length === 0) return ''
  return items.map((i) => i.name).join(', ')
}

export type WorkPermitFormModalProps = {
  visible: boolean
  title: string
  workPermitId?: number
  initial?: WorkPermitFormState
  isCrew: boolean
  vessels: Vessel[]
  companies: CompanyGroup[]
  subcompanies: Subcompany[]
  isTopLevel: boolean
  effectiveCompanyId?: number
  defaultVesselId?: number
  permitNameOptions?: string[]
  onClose: () => void
  onSave: (data: WorkPermitFormState, uploads: WorkPermitUploadFiles) => void
  onSubmit: (data: WorkPermitFormState, uploads: WorkPermitUploadFiles) => void
}

export const isEnclosedSpaceOrHotWork = (name?: string) => {
  if (!name) return false
  const lower = name.toLowerCase()
  return lower.includes('enclosed space') || lower.includes('hot work')
}

export const calculateValidityHours = (
  startDate?: string,
  startTime?: string,
  endDate?: string,
  endTime?: string
): number | null => {
  if (!startDate || !startTime || !endDate || !endTime) return null
  const s = new Date(`${startDate}T${startTime}`)
  const e = new Date(`${endDate}T${endTime}`)
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return null
  const diffMs = e.getTime() - s.getTime()
  if (diffMs < 0) return null
  return Math.round((diffMs / (1000 * 60 * 60)) * 10) / 10
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'

export const WorkPermitFormModal: FC<WorkPermitFormModalProps> = ({
  visible,
  title,
  workPermitId,
  initial,
  isCrew,
  vessels,
  companies,
  subcompanies,
  isTopLevel,
  effectiveCompanyId,
  defaultVesselId,
  permitNameOptions,
  onClose,
  onSave,
  onSubmit,
}) => {
  const isAddMode = !workPermitId
  const [formData, setFormData] = useState<WorkPermitFormState>(initial || emptyForm)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [companyId, setCompanyId] = useState<string>('')
  const [subcompanyId, setSubcompanyId] = useState<string>('')
  const [showExtensionControls, setShowExtensionControls] = useState<boolean>(false)
  const [existingAttachments, setExistingAttachments] = useState<WorkPermitExistingAttachments>(
    emptyExistingAttachments
  )
  const [uploadFiles, setUploadFiles] = useState<WorkPermitUploadFiles>(emptyUploads)
  const [deletingAttachment, setDeletingAttachment] = useState<Record<string, boolean>>({})
  const [crews, setCrews] = useState<CrewLite[]>([])
  const [loadingCrews, setLoadingCrews] = useState<boolean>(false)

  const permitOptions = useMemo(() => {
    return STATIC_FORMS.map((f) => {
      const label = `${f.code.toUpperCase()} - ${f.title}`
      return {code: f.code.toUpperCase(), label}
    })
  }, [])

  useEffect(() => {
    if (!visible) return
    let active = true
    const fetchCrews = async () => {
      const vId = formData.vesselId
      if (!vId) {
        setCrews([])
        return
      }
      try {
        setLoadingCrews(true)
        const list = await listCrewsLite(Number(vId))
        if (!active) return
        const filtered = (Array.isArray(list) ? list : []).filter(
          (c) => Number(c.vesselId) === Number(vId)
        )
        setCrews(filtered)
        setFormData((prev) => {
          let changed = false
          const next = {...prev}
          if (next.leadPerson && !next.leadPersonRank) {
            const match = filtered.find((c) => c.fullName === next.leadPerson)
            if (match?.rankName) {
              next.leadPersonRank = match.rankName
              changed = true
            }
          }
          if (next.personResponsible && !next.personResponsibleRank) {
            const match = filtered.find((c) => c.fullName === next.personResponsible)
            if (match?.rankName) {
              next.personResponsibleRank = match.rankName
              changed = true
            }
          }
          return changed ? next : prev
        })
      } catch (err) {
        console.error('Failed to load crew for vessel', err)
        if (active) setCrews([])
      } finally {
        if (active) setLoadingCrews(false)
      }
    }
    fetchCrews()
    return () => {
      active = false
    }
  }, [visible, formData.vesselId])

  useEffect(() => {
    if (!visible) return

    const now = new Date()
    const end = new Date(now.getTime() + 8 * 60 * 60 * 1000)

    const fmtDate = (d: Date) => {
      const yyyy = d.getFullYear()
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      return `${yyyy}-${mm}-${dd}`
    }
    const fmtTime = (d: Date) => {
      const hh = String(d.getHours()).padStart(2, '0')
      const min = String(d.getMinutes()).padStart(2, '0')
      return `${hh}:${min}`
    }

    const initialPermitName = initial?.permitName || ''
    const isInitialEndReq = isEnclosedSpaceOrHotWork(initialPermitName)

    setFormData((prev) => ({
      ...(initial || emptyForm),
      vesselId: initial?.vesselId ?? prev.vesselId ?? (defaultVesselId ?? null),
      startDate: initial?.startDate ?? fmtDate(now),
      startTime: initial?.startTime ?? fmtTime(now),
      expectedEndDate: initial?.expectedEndDate ?? (isInitialEndReq ? fmtDate(end) : ''),
      expectedEndTime: initial?.expectedEndTime ?? (isInitialEndReq ? fmtTime(end) : ''),
      status: (initial?.status as any) ?? 'SAVE',
    }))

    const initialHours = calculateValidityHours(
      initial?.startDate,
      initial?.startTime,
      initial?.expectedEndDate,
      initial?.expectedEndTime
    )
    setShowExtensionControls(Boolean(initialHours && initialHours > 8.01))

    setErrors({})
    setExistingAttachments({
      permitFormFiles: initial?.permitFormFiles || [],
      officePermitFiles: initial?.officePermitFiles || [],
      completedFormFiles: initial?.completedFormFiles || [],
    })
    setUploadFiles(emptyUploads)

    setCompanyId((prev) => {
      if (prev) return prev
      if (!isTopLevel && effectiveCompanyId) return String(effectiveCompanyId)
      return prev || ''
    })
    setSubcompanyId('')
  }, [visible, initial, defaultVesselId, isTopLevel, effectiveCompanyId])

  useEffect(() => {
    if (!visible) return
    if (!formData.vesselId) return
    const v = vessels.find((vv) => Number(vv.id) === Number(formData.vesselId)) as any
    if (!v) return
    const cgaId = v.companyGroupAdmin?.id ?? v.companyGroupId ?? v.cgaid?.id ?? null
    const caId = v.companyAdmin?.id ?? v.companyId ?? null
    if (cgaId != null) setCompanyId(String(cgaId))
    if (caId != null) setSubcompanyId(String(caId))
  }, [visible, formData.vesselId, vessels])

  const vesselsForModal = useMemo(() => {
    let list = vessels
    if (companyId) {
      const cid = Number(companyId)
      list = list.filter((v: any) => Number(v.companyGroupAdmin?.id ?? v.companyGroupId) === cid)
    }
    if (subcompanyId) {
      const scid = Number(subcompanyId)
      list = list.filter((v: any) => Number(v.companyAdmin?.id ?? v.companyId) === scid)
    }
    return list
  }, [vessels, companyId, subcompanyId])

  const subcompaniesForCompany = useMemo(() => {
    if (!companyId) return []
    const cid = Number(companyId)
    return subcompanies.filter((sc) => sc.companyId === cid)
  }, [companyId, subcompanies])

  const hasSubcompaniesForCompany = subcompaniesForCompany.length > 0

  useEffect(() => {
    if (!visible) return
    setFormData((prev) => {
      if (!prev.vesselId) return prev
      const exists = vesselsForModal.some((v) => Number(v.id) === Number(prev.vesselId))
      return exists ? prev : {...prev, vesselId: null}
    })
  }, [visible, vesselsForModal])

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const {name, value, type} = e.target
    const val = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    setFormData((prev) => {
      const next = {...prev, [name]: val}

      if (name === 'vesselId' && Number(val) !== Number(prev.vesselId)) {
        next.leadPerson = ''
        next.leadPersonRank = ''
        next.personResponsible = ''
        next.personResponsibleRank = ''
      }

      if (name === 'leadPerson') {
        const found = crews.find((c) => c.fullName === val)
        next.leadPersonRank = found?.rankName || ''
      }

      if (name === 'personResponsible') {
        const found = crews.find((c) => c.fullName === val)
        next.personResponsibleRank = found?.rankName || ''
      }

      if (isFirstFill && (name === 'startDate' || name === 'startTime')) {
        const startDate = name === 'startDate' ? String(val) : prev.startDate
        const startTime = name === 'startTime' ? String(val) : prev.startTime

        if (startDate && startTime && isEnclosedSpaceOrHotWork(prev.permitName)) {
          const [y, m, d] = startDate.split('-').map(Number)
          const [hh, mm] = startTime.split(':').map(Number)
          if ([y, m, d, hh, mm].every((n) => Number.isFinite(n))) {
            const start = new Date(y, m - 1, d, hh, mm, 0, 0)
            const end = new Date(start.getTime() + 8 * 60 * 60 * 1000)
            const endDate = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(
              end.getDate()
            ).padStart(2, '0')}`
            const endTime = `${String(end.getHours()).padStart(2, '0')}:${String(
              end.getMinutes()
            ).padStart(2, '0')}`
            next.expectedEndDate = endDate
            next.expectedEndTime = endTime
          }
        }
      }

      return next
    })
    if (errors[name]) {
      setErrors((prev) => ({...prev, [name]: ''}))
    }
  }

  const handleFilesChange = (name: keyof WorkPermitUploadFiles, files: FileList | null) => {
    const list = files ? Array.from(files) : []
    setUploadFiles((prev) => ({...prev, [name]: list}))
    const items = list.map((f) => ({name: f.name}))
    setFormData((prev) => ({
      ...prev,
      [name]: [...((prev as any)[name] || []), ...items],
    }))
  }

  const attachmentTypeFor = (key: keyof WorkPermitUploadFiles): WorkPermitAttachmentType => {
    switch (key) {
      case 'permitFormFiles':
        return 'FORM'
      case 'officePermitFiles':
        return 'OFFICE'
      case 'completedFormFiles':
        return 'COMPLETED'
    }
  }

  const removeAttachment = async (key: keyof WorkPermitUploadFiles, fileName: string) => {
    // Remove newly-selected file (local)
    const isNew = uploadFiles[key].some((f) => f.name === fileName)
    if (isNew) {
      setUploadFiles((prev) => ({
        ...prev,
        [key]: prev[key].filter((f) => f.name !== fileName),
      }))
      setFormData((prev) => ({
        ...prev,
        [key]: (prev as any)[key]?.filter((x: any) => x?.name !== fileName) ?? [],
      }))
      return
    }

    // Remove already-saved attachment (edit mode -> server delete)
    const isExisting = existingAttachments[key].some((x) => x.name === fileName)
    if (!isExisting) return
    if (!workPermitId) return

    const token = `${key}:${fileName}`
    setDeletingAttachment((prev) => ({...prev, [token]: true}))
    try {
      await deleteWorkPermitAttachment(workPermitId, attachmentTypeFor(key), fileName)
      setExistingAttachments((prev) => ({
        ...prev,
        [key]: prev[key].filter((x) => x.name !== fileName),
      }))
      setFormData((prev) => ({
        ...prev,
        [key]: (prev as any)[key]?.filter((x: any) => x?.name !== fileName) ?? [],
      }))
    } catch (e) {
      console.error('Failed to delete attachment', e)
    } finally {
      setDeletingAttachment((prev) => ({...prev, [token]: false}))
    }
  }

  const renderAttachmentChips = (key: keyof WorkPermitUploadFiles) => {
    const items: {name: string}[] = (formData as any)[key] || []
    if (!items.length) return <div className='text-muted small mt-1'>No files</div>

    return (
      <div className='d-flex flex-wrap gap-2 mt-2'>
        {items.map((it) => {
          const token = `${key}:${it.name}`
          const busy = Boolean(deletingAttachment[token])
          const disabled = busy || isClosed

          return (
            <span
              key={it.name}
              className='badge text-dark'
              style={{background: '#f1f1f2', fontWeight: 500}}
              title={it.name}
            >
              {it.name}
              {!disabled && (
                <span
                  role='button'
                  tabIndex={0}
                  aria-label={`Remove ${it.name}`}
                  className='ms-2'
                  style={{cursor: 'pointer', color: '#d9214e', fontWeight: 700}}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    removeAttachment(key, it.name)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      removeAttachment(key, it.name)
                    }
                  }}
                >
                  ×
                </span>
              )}
              {disabled && busy && <span className='ms-2 text-muted'>…</span>}
            </span>
          )
        })}
      </div>
    )
  }

  const canEditAfterSubmit = formData.status === 'SUBMIT'
  const isClosed = formData.status === 'CLOSE'
  const isLocked = formData.status !== 'SAVE'
  const isFirstFill = !initial

  const validityHours = useMemo(() => {
    return calculateValidityHours(
      formData.startDate,
      formData.startTime,
      formData.expectedEndDate,
      formData.expectedEndTime
    )
  }, [formData.startDate, formData.startTime, formData.expectedEndDate, formData.expectedEndTime])

  const isExtended = validityHours !== null && validityHours > 8.01
  const canEditEndTime = !isClosed && (isFirstFill || showExtensionControls || isExtended || canEditAfterSubmit)

  const extendByHours = (addHours: number) => {
    const sDate = formData.startDate
    const sTime = formData.startTime
    if (!sDate || !sTime) return
    const [y, m, d] = sDate.split('-').map(Number)
    const [hh, mm] = sTime.split(':').map(Number)
    if (![y, m, d, hh, mm].every((n) => Number.isFinite(n))) return
    const start = new Date(y, m - 1, d, hh, mm, 0, 0)
    const totalHours = Math.min(24, 8 + addHours)
    const targetEnd = new Date(start.getTime() + totalHours * 60 * 60 * 1000)
    const endY = targetEnd.getFullYear()
    const endM = String(targetEnd.getMonth() + 1).padStart(2, '0')
    const endD = String(targetEnd.getDate()).padStart(2, '0')
    const endH = String(targetEnd.getHours()).padStart(2, '0')
    const endMin = String(targetEnd.getMinutes()).padStart(2, '0')

    setFormData((prev) => ({
      ...prev,
      expectedEndDate: `${endY}-${endM}-${endD}`,
      expectedEndTime: `${endH}:${endMin}`,
    }))
    setShowExtensionControls(true)
    setErrors((prev) => {
      const copy = {...prev}
      delete copy.expectedEndDate
      delete copy.expectedEndTime
      return copy
    })
  }

  const resetToStandard8Hours = () => {
    extendByHours(0)
    setShowExtensionControls(false)
  }

  const validate = () => {
    const next: Record<string, string> = {}
    if (!formData.vesselId) next.vesselId = 'Vessel is required'
    if (!formData.permitName.trim()) next.permitName = 'Permit name and form number is required'
    if (!formData.startDate) next.startDate = 'Start date is required'
    if (!formData.startTime) next.startTime = 'Start time is required'

    if (isEnclosedSpaceOrHotWork(formData.permitName)) {
      if (!formData.expectedEndDate) next.expectedEndDate = 'End date is required'
      if (!formData.expectedEndTime) next.expectedEndTime = 'End time is required'

      if (formData.startDate && formData.startTime && formData.expectedEndDate && formData.expectedEndTime) {
        const start = new Date(`${formData.startDate}T${formData.startTime}`)
        const end = new Date(`${formData.expectedEndDate}T${formData.expectedEndTime}`)
        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
          next.expectedEndTime = 'Invalid date or time'
        } else if (end <= start) {
          next.expectedEndTime = 'End time must be after start time'
        } else {
          const diffHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60)
          if (diffHours > 24.01) {
            next.expectedEndTime = 'Permit validity cannot exceed 24 hours (with extension)'
          }
        }
      }
    }

    setErrors(next)
    return Object.keys(next).length === 0
  }

  const getNormalizedFormData = (): WorkPermitFormState => {
    let formNumber = formData.formNumber
    if (!formNumber || !formNumber.trim()) {
      const matched = STATIC_FORMS.find(
        (f) =>
          `${f.code.toUpperCase()} - ${f.title}` === formData.permitName ||
          f.code.toUpperCase() === formData.permitName
      )
      if (matched) {
        formNumber = matched.code.toUpperCase()
      } else if (formData.permitName.includes(' - ')) {
        formNumber = formData.permitName.split(' - ')[0].trim()
      } else {
        formNumber = formData.permitName.trim() || '-'
      }
    }
    const requiresEnd = isEnclosedSpaceOrHotWork(formData.permitName)
    return {
      ...formData,
      formNumber,
      expectedEndDate: requiresEnd ? formData.expectedEndDate : '',
      expectedEndTime: requiresEnd ? formData.expectedEndTime : '',
    }
  }

  const handleSave = () => {
    if (!validate()) return
    onSave(getNormalizedFormData(), uploadFiles)
  }

  const handleSubmit = () => {
    if (!validate()) return
    onSubmit(getNormalizedFormData(), uploadFiles)
  }

  if (!visible) return null

  const WRAP_STYLE: React.CSSProperties = {
    background: 'rgba(0,0,0,0.5)',
    position: 'fixed',
    inset: 0,
    zIndex: 1050,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  return (
    <div style={WRAP_STYLE} tabIndex={-1}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{
          width: '70vw',
          height: '75vh',
          maxWidth: '1100px',
          maxHeight: '850px',
        }}
      >
        {/* Header */}
        <div className='d-flex align-items-center justify-content-between border-bottom px-5 py-4 flex-shrink-0'>
          <h5 className='modal-title text-dark m-0'>{title}</h5>
          <button type='button' className='btn-close m-0' onClick={onClose} />
        </div>

        {/* Body with inner scroll */}
        <div className='flex-grow-1 overflow-auto px-5 py-4'>
          <div className='row g-4'>
            {!isCrew && (
              <>
                {isTopLevel && (
                  <div className='col-md-4'>
                    <label className={LABEL}>Company</label>
                    <SearchableSelect
                      className='form-select'
                      value={companyId}
                      onChange={(e) => {
                        setCompanyId(e.target.value)
                        setSubcompanyId('')
                      }}
                      disabled={isLocked}
                    >
                      <option value=''>All</option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                )}

                {Boolean(companyId) && hasSubcompaniesForCompany && (
                  <div className='col-md-4'>
                    <label className={LABEL}>Sub Company</label>
                    <SearchableSelect
                      className='form-select'
                      value={subcompanyId}
                      onChange={(e) => setSubcompanyId(e.target.value)}
                      disabled={isLocked}
                    >
                      <option value=''>All</option>
                      {subcompaniesForCompany.map((sc) => (
                        <option key={sc.id} value={sc.id}>
                          {sc.name}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                )}
              </>
            )}

            {!isCrew && (
              <div className='col-md-4'>
                <label className={LABEL}>
                  Vessel <span className='text-danger'>*</span>
                </label>
                <SearchableSelect
                  className='form-select'
                  name='vesselId'
                  value={formData.vesselId ?? ''}
                  onChange={handleInputChange}
                  disabled={isLocked}
                >
                  <option value=''>Select Vessel</option>
                  {vesselsForModal.map((v) => (
                    <option key={v.id} value={v.id}>
                      {(v as any).fleet_name || (v as any).name || `Vessel #${v.id}`}
                    </option>
                  ))}
                </SearchableSelect>
                {errors.vesselId && <div className='text-danger small mt-1'>{errors.vesselId}</div>}
              </div>
            )}

            {isCrew && !isAddMode && (
              <div className='col-md-4'>
                <label className={LABEL}>
                  Vessel <span className='text-danger'>*</span>
                </label>
                <div className='form-control' style={{background: '#f8f9fa'}}>
                  {(vesselsForModal.find((v) => Number(v.id) === Number(formData.vesselId)) as any)
                    ?.fleet_name ||
                    vesselsForModal.find((v) => Number(v.id) === Number(formData.vesselId))?.name ||
                    (vessels.find((v) => Number(v.id) === Number(formData.vesselId)) as any)
                      ?.fleet_name ||
                    vessels.find((v) => Number(v.id) === Number(formData.vesselId))?.name ||
                    '-'}
                </div>
              </div>
            )}

            <div className='col-md-12'>
              <label className={LABEL}>
                Permit Name and Form Number <span className='text-danger'>*</span>
              </label>
              <SearchableSelect
                className='form-select'
                name='permitName'
                value={formData.permitName}
                onChange={(e) => {
                  const val = e.target.value
                  const matched = STATIC_FORMS.find(
                    (f) =>
                      `${f.code.toUpperCase()} - ${f.title}` === val ||
                      f.code.toUpperCase() === val
                  )
                  const derivedFormNumber = matched
                    ? matched.code.toUpperCase()
                    : val.includes(' - ')
                    ? val.split(' - ')[0].trim()
                    : val
                  const requiresEnd = isEnclosedSpaceOrHotWork(val)
                  setFormData((prev) => {
                    const next = {
                      ...prev,
                      permitName: val,
                      formNumber: derivedFormNumber,
                    }
                    if (requiresEnd && (!next.expectedEndDate || !next.expectedEndTime)) {
                      const sDate = next.startDate || prev.startDate
                      const sTime = next.startTime || prev.startTime
                      if (sDate && sTime) {
                        const [y, m, d] = sDate.split('-').map(Number)
                        const [hh, mm] = sTime.split(':').map(Number)
                        if ([y, m, d, hh, mm].every((n) => Number.isFinite(n))) {
                          const start = new Date(y, m - 1, d, hh, mm, 0, 0)
                          const end = new Date(start.getTime() + 8 * 60 * 60 * 1000)
                          next.expectedEndDate = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(
                            end.getDate()
                          ).padStart(2, '0')}`
                          next.expectedEndTime = `${String(end.getHours()).padStart(2, '0')}:${String(
                            end.getMinutes()
                          ).padStart(2, '0')}`
                        }
                      }
                    } else if (!requiresEnd) {
                      next.expectedEndDate = ''
                      next.expectedEndTime = ''
                    }
                    return next
                  })
                  if (errors.permitName) {
                    setErrors((prev) => ({...prev, permitName: ''}))
                  }
                  if (!requiresEnd) {
                    setErrors((prev) => {
                      const copy = {...prev}
                      delete copy.expectedEndDate
                      delete copy.expectedEndTime
                      return copy
                    })
                  }
                }}
                disabled={isLocked}
              >
                <option value=''>Select Permit Name and Form Number</option>
                {permitOptions.map((opt) => (
                  <option key={opt.code} value={opt.label}>
                    {opt.label}
                  </option>
                ))}
                {Boolean(formData.permitName) &&
                  !permitOptions.some((opt) => opt.label === formData.permitName) && (
                    <option value={formData.permitName}>
                      {formData.permitName}
                    </option>
                  )}
              </SearchableSelect>
              {errors.permitName && <div className='text-danger small mt-1'>{errors.permitName}</div>}
            </div>

            {isEnclosedSpaceOrHotWork(formData.permitName) ? (
              <>
                <div className='col-md-3'>
                  <label className={LABEL}>
                    Start Date <span className='text-danger'>*</span>
                  </label>
                  <input
                    type='date'
                    className={`form-control ${errors.startDate ? 'is-invalid' : ''}`}
                    name='startDate'
                    value={formData.startDate}
                    onChange={handleInputChange}
                    disabled={!isFirstFill}
                  />
                  {errors.startDate && <div className='text-danger small mt-1'>{errors.startDate}</div>}
                </div>
                <div className='col-md-3'>
                  <label className={LABEL}>
                    Start Time <span className='text-danger'>*</span>
                  </label>
                  <input
                    type='time'
                    className={`form-control ${errors.startTime ? 'is-invalid' : ''}`}
                    name='startTime'
                    value={formData.startTime}
                    onChange={handleInputChange}
                    disabled={!isFirstFill}
                  />
                  {errors.startTime && <div className='text-danger small mt-1'>{errors.startTime}</div>}
                </div>

                <div className='col-md-3'>
                  <label className={LABEL}>
                    End Date <span className='text-danger'>*</span>
                  </label>
                  <input
                    type='date'
                    className={`form-control ${errors.expectedEndDate ? 'is-invalid' : ''}`}
                    name='expectedEndDate'
                    value={formData.expectedEndDate}
                    onChange={handleInputChange}
                    disabled={!canEditEndTime}
                  />
                  {errors.expectedEndDate && (
                    <div className='text-danger small mt-1'>{errors.expectedEndDate}</div>
                  )}
                  <div className='text-muted small mt-1'>
                    {isExtended ? 'Extended beyond 8 hours.' : 'Standard 8 hours validity.'}
                  </div>
                </div>
                <div className='col-md-3'>
                  <label className={LABEL}>
                    End Time <span className='text-danger'>*</span>
                  </label>
                  <input
                    type='time'
                    className={`form-control ${errors.expectedEndTime ? 'is-invalid' : ''}`}
                    name='expectedEndTime'
                    value={formData.expectedEndTime}
                    onChange={handleInputChange}
                    disabled={!canEditEndTime}
                  />
                  {errors.expectedEndTime && (
                    <div className='text-danger small mt-1'>{errors.expectedEndTime}</div>
                  )}
                  {validityHours !== null && (
                    <div className='text-muted small mt-1'>
                      Duration: <strong>{validityHours}h</strong>
                      {isExtended && (
                        <span className='text-primary fw-semibold ms-1'>
                          (+{Math.round((validityHours - 8) * 10) / 10}h extended)
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Time Slot Extension Control */}
                <div className='col-12 mt-1 mb-2'>
                  <div className='p-3 rounded border' style={{backgroundColor: '#f8fafd', borderColor: '#d0e1fd'}}>
                    <div className='d-flex align-items-center justify-content-between flex-wrap gap-2'>
                      <div className='d-flex align-items-center gap-2 flex-wrap'>
                        <span className='fw-bold text-dark fs-7'>Time Slot Extension</span>
                        <span className='text-muted fs-8'>
                          (Standard: 8 hrs • Max validity with extension: 24 hrs)
                        </span>
                        {isExtended && (
                          <span className='badge badge-light-primary fw-bold fs-8 px-2 py-1'>
                            Extended: {validityHours}h Total
                          </span>
                        )}
                      </div>

                      {!isClosed && (
                        <button
                          type='button'
                          className={`btn btn-sm ${showExtensionControls ? 'btn-secondary' : 'btn-primary'} py-1 px-3`}
                          onClick={() => setShowExtensionControls((prev) => !prev)}
                        >
                          {showExtensionControls ? 'Hide Extension Controls' : '+ Extend Time Slot'}
                        </button>
                      )}
                    </div>

                    {(showExtensionControls || isExtended) && !isClosed && (
                      <div className='d-flex align-items-center gap-2 flex-wrap pt-2 mt-2 border-top'>
                        <span className='text-muted fs-8 fw-semibold me-1'>Quick Extend:</span>
                        <button
                          type='button'
                          className='btn btn-sm btn-white border py-1 px-2 fs-8'
                          onClick={() => extendByHours(2)}
                          title='Extend by 2 hours (10 hours total)'
                        >
                          + 2 Hours (10h total)
                        </button>
                        <button
                          type='button'
                          className='btn btn-sm btn-white border py-1 px-2 fs-8'
                          onClick={() => extendByHours(4)}
                          title='Extend by 4 hours (12 hours total)'
                        >
                          + 4 Hours (12h total)
                        </button>
                        <button
                          type='button'
                          className='btn btn-sm btn-white border py-1 px-2 fs-8'
                          onClick={() => extendByHours(8)}
                          title='Extend by 8 hours (16 hours total)'
                        >
                          + 8 Hours (16h total)
                        </button>
                        <button
                          type='button'
                          className='btn btn-sm btn-white border py-1 px-2 fs-8'
                          onClick={() => extendByHours(16)}
                          title='Extend to maximum 24 hours'
                        >
                          + Max (24h total)
                        </button>

                        {isExtended && (
                          <button
                            type='button'
                            className='btn btn-sm btn-light-danger border py-1 px-2 fs-8 ms-auto'
                            onClick={resetToStandard8Hours}
                          >
                            Reset to Standard (8h)
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className='col-md-6'>
                  <label className={LABEL}>
                    Start Date <span className='text-danger'>*</span>
                  </label>
                  <input
                    type='date'
                    className={`form-control ${errors.startDate ? 'is-invalid' : ''}`}
                    name='startDate'
                    value={formData.startDate}
                    onChange={handleInputChange}
                    disabled={!isFirstFill}
                  />
                  {errors.startDate && <div className='text-danger small mt-1'>{errors.startDate}</div>}
                </div>
                <div className='col-md-6'>
                  <label className={LABEL}>
                    Start Time <span className='text-danger'>*</span>
                  </label>
                  <input
                    type='time'
                    className={`form-control ${errors.startTime ? 'is-invalid' : ''}`}
                    name='startTime'
                    value={formData.startTime}
                    onChange={handleInputChange}
                    disabled={!isFirstFill}
                  />
                  {errors.startTime && <div className='text-danger small mt-1'>{errors.startTime}</div>}
                </div>
              </>
            )}

            <div className='col-md-6'>
              <label className={LABEL}>Lead Person</label>
              <SearchableSelect
                className='form-select'
                name='leadPerson'
                value={formData.leadPerson || ''}
                onChange={handleInputChange}
                disabled={isLocked || !formData.vesselId}
              >
                <option value=''>
                  {!formData.vesselId
                    ? 'Select Vessel First'
                    : loadingCrews
                    ? 'Loading crew...'
                    : 'Select Lead Person'}
                </option>
                {crews.map((c) => (
                  <option key={c.id} value={c.fullName}>
                    {c.fullName}
                    {c.rankName ? ` (${c.rankName})` : ''}
                  </option>
                ))}
                {formData.leadPerson && !crews.some((c) => c.fullName === formData.leadPerson) && (
                  <option value={formData.leadPerson}>{formData.leadPerson}</option>
                )}
              </SearchableSelect>
            </div>

            <div className='col-md-6'>
              <label className={LABEL}>Lead Person Rank</label>
              <input
                type='text'
                className='form-control bg-light'
                name='leadPersonRank'
                value={formData.leadPersonRank || ''}
                readOnly
                disabled={isLocked}
                placeholder='Auto-filled from selected crew'
              />
            </div>

            <div className='col-md-6'>
              <label className={LABEL}>Person Responsible</label>
              <SearchableSelect
                className='form-select'
                name='personResponsible'
                value={formData.personResponsible || ''}
                onChange={handleInputChange}
                disabled={isLocked || !formData.vesselId}
              >
                <option value=''>
                  {!formData.vesselId
                    ? 'Select Vessel First'
                    : loadingCrews
                    ? 'Loading crew...'
                    : 'Select Person Responsible'}
                </option>
                {crews.map((c) => (
                  <option key={c.id} value={c.fullName}>
                    {c.fullName}
                    {c.rankName ? ` (${c.rankName})` : ''}
                  </option>
                ))}
                {formData.personResponsible && !crews.some((c) => c.fullName === formData.personResponsible) && (
                  <option value={formData.personResponsible}>{formData.personResponsible}</option>
                )}
              </SearchableSelect>
            </div>

            <div className='col-md-6'>
              <label className={LABEL}>Person Responsible Rank</label>
              <input
                type='text'
                className='form-control bg-light'
                name='personResponsibleRank'
                value={formData.personResponsibleRank || ''}
                readOnly
                disabled={isLocked}
                placeholder='Auto-filled from selected crew'
              />
            </div>

            <div className='col-md-6'>
              <label className={LABEL}>Office Permission Required</label>
              <SearchableSelect
                className='form-select'
                name='officePermissionRequired'
                value={formData.officePermissionRequired ? 'yes' : 'no'}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    officePermissionRequired: e.target.value === 'yes',
                  }))
                }
                disabled={isLocked}
              >
                <option value='no'>No</option>
                <option value='yes'>Yes</option>
              </SearchableSelect>
            </div>

            {!isCrew && formData.officePermissionRequired && (
              <div className='col-md-6'>
                <label className={LABEL}>Office Approved</label>
                <SearchableSelect
                  className='form-select'
                  name='officeApproved'
                  value={formData.officeApproved ? 'yes' : 'no'}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      officeApproved: e.target.value === 'yes',
                    }))
                  }
                  disabled={!canEditAfterSubmit}
                >
                  <option value='no'>No</option>
                  <option value='yes'>Yes</option>
                </SearchableSelect>
                {!canEditAfterSubmit && (
                  <div className='text-muted small mt-1'>Approval available after submission.</div>
                )}
              </div>
            )}

            <div className='col-12 mt-4'>
              <h6 className='fw-semibold'>Attachments</h6>
            </div>

            <div className='col-md-6'>
              <label className={LABEL}>Permit Form</label>
              <input
                type='file'
                className='form-control'
                multiple
                onChange={(e) => handleFilesChange('permitFormFiles', e.target.files)}
                disabled={isClosed}
              />
              {renderAttachmentChips('permitFormFiles')}
            </div>

            {formData.officePermissionRequired && (
              <div className='col-md-6'>
                <label className={LABEL}>Office Permits</label>
                <input
                  type='file'
                  className='form-control'
                  multiple
                  onChange={(e) => handleFilesChange('officePermitFiles', e.target.files)}
                  disabled={isClosed}
                />
                {renderAttachmentChips('officePermitFiles')}
              </div>
            )}

            <div className='col-md-6'>
              <label className={LABEL}>Completed Form</label>
              <input
                type='file'
                className='form-control'
                multiple
                onChange={(e) => handleFilesChange('completedFormFiles', e.target.files)}
                disabled={isClosed}
              />
              {renderAttachmentChips('completedFormFiles')}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className='d-flex align-items-center justify-content-between border-top px-5 py-4 flex-shrink-0'>
          <div className='text-muted small'>
            {isEnclosedSpaceOrHotWork(formData.permitName)
              ? 'Standard validity is 8 hours. Time slot extension allows up to 24 hours maximum.'
              : ''}
          </div>
          <div className='d-flex gap-2'>
            <button type='button' className='btn btn-light' onClick={onClose}>
              Cancel
            </button>
            <button type='button' className='btn btn-primary' onClick={handleSave} disabled={isClosed}>
              Save
            </button>
            <button type='button' className='btn btn-success' onClick={handleSubmit} disabled={isClosed}>
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
